import { gql, useQuery } from '@apollo/client'
import * as _ from 'lodash'
import moment from 'moment'
import { useMemo } from 'react'

import { useDateFilter } from '../../../dateFilter'
import { useGroupFilter } from '../../../groupFilter'
import calc from '../../../utils/calc'
import { corporateGroupTableConfigs } from '../../../variables'
import { IApiDataType } from '../../types'
import buildMetricValuesHook from '../utils/buildMetricValuesHook'

type IMetricDataType = Record<
  string,
  {
    name: string
    unit: 'CENT' | 'PERCENTAGE' | 'DOLLAR' | 'COUNT' | 'SECONDS' | 'SCORE'
    value: number
  }
>

interface IStoreDetailsType {
  listLocationGroupDateRangeMetricsValues?: {
    nodes: {
      type: string
      locationGroupId: number
      metricData: IMetricDataType
      metricSummaryData: IMetricDataType
    }[]
  }
  listLocationDateRangeMetricsValues?: {
    nodes: {
      type: string
      locationId: number
      metricData: IMetricDataType
      metricSummaryData: IMetricDataType
    }[]
  }
  listLocationMarketAndOpenDate?: {
    nodes: {
      locationId: number
      openedAt: string
      market: string
      ro: string
    }[]
  }
}

export const fwWingstopSalesAndCompTicketsConfigs = {
  ...corporateGroupTableConfigs,
  averageNetSalesTrailing7Days: 'price',
  averageNetSalesTrailing7DaysLastYear: 'price',
  netSales: 'price',
  netSalesSameDayLastYear: 'price',
  netSalesSameDay2YearsAgo: 'percent',
  netSales7DaysAgo: 'price',
  netSalesPercentageDiffTo7DaysAgo: 'percent',
  netSalesPercentageDiffToLastYear: 'percent',
  netSalesPercentageDiffTo2YearsAgo: 'percent',
  checkCount: 'number',
  checkCountSameDayLastYear: 'number',
  checkCountSameDay2YearsAgo: 'number',
  checkCount7DaysAgo: 'number',
  checkCountPercentageDiffTo7DaysAgo: 'percent',
  checkCountPercentageDiffToLastYear: 'percent',
  checkCountPercentageDiffTo2YearsAgo: 'percent',
  checkAverage: 'price',
  checkAverageSameDayLastYear: 'price',
  checkAverageSameDay2YearsAgo: 'price',
  checkAverage7DaysAgo: 'price',
  checkAveragePercentageDiffTo7DaysAgo: 'percent',
  checkAveragePercentageDiffToLastYear: 'percent',
  checkAveragePercentageDiffTo2YearsAgo: 'percent',
  t7NetSales: 'price',
  t7NetSalesSameDayLastYear: 'price',
  t7NetSalesPercentageDiffToLastYear: 'percent',
  t7NetSalesPercentageDiffTo2YearsAgo: 'percent',
  t7CheckCount: 'number',
  t7CheckCountPercentageDiffToLastYear: 'percent',
  t7CheckCountPercentageDiffTo2YearsAgo: 'percent',
  t7CheckAverage: 'price',
  t7CheckAveragePercentageDiffToLastYear: 'percent',
  t7CheckAveragePercentageDiffTo2YearsAgo: 'percent',
  t30NetSales: 'price',
  t30NetSalesSameDayLastYear: 'price',
  t30NetSalesPercentageDiffToLastYear: 'percent',
  t30NetSalesPercentageDiffTo2YearsAgo: 'percent',
  t30CheckCount: 'number',
  t30CheckCountPercentageDiffToLastYear: 'percent',
  t30CheckCountPercentageDiffTo2YearsAgo: 'percent',
  t30CheckAverage: 'price',
  t30CheckAveragePercentageDiffToLastYear: 'percent',
  t30CheckAveragePercentageDiffTo2YearsAgo: 'percent',
  ttmNetSales: 'price',
  ttmNetSalesSameDayLastYear: 'price',
  ttmNetSalesPercentageDiffToLastYear: 'percent',
  ttmNetSalesPercentageDiffTo2YearsAgo: 'percent',
  ttmCheckCount: 'number',
  ttmCheckCountPercentageDiffToLastYear: 'percent',
  ttmCheckCountPercentageDiffTo2YearsAgo: 'percent',
  ttmCheckAverage: 'price',
  ttmCheckAveragePercentageDiffToLastYear: 'percent',
  ttmCheckAveragePercentageDiffTo2YearsAgo: 'percent',
  pwkNetSales: 'price',
  pwkNetSalesSameDayLastYear: 'price',
  pwkNetSalesPercentageDiffToLastYear: 'percent',
  pwkNetSalesPercentageDiffTo2YearsAgo: 'percent',
  pwkCheckCount: 'number',
  pwkCheckCountPercentageDiffToLastYear: 'percent',
  pwkCheckCountPercentageDiffTo2YearsAgo: 'percent',
  pwkCheckAverage: 'price',
  pwkCheckAveragePercentageDiffToLastYear: 'percent',
  pwkCheckAveragePercentageDiffTo2YearsAgo: 'percent',
  wtdNetSales: 'price',
  wtdNetSalesSameDayLastYear: 'price',
  wtdNetSalesPercentageDiffToLastYear: 'percent',
  wtdNetSalesPercentageDiffTo2YearsAgo: 'percent',
  wtdNetSales7DaysAgo: 'price',
  wtdCheckCount: 'number',
  wtdCheckCountPercentageDiffToLastYear: 'percent',
  wtdCheckCountPercentageDiffTo2YearsAgo: 'percent',
  wtdCheckAverage: 'price',
  wtdCheckAveragePercentageDiffToLastYear: 'percent',
  wtdCheckAveragePercentageDiffTo2YearsAgo: 'percent',
  ptdNetSales: 'price',
  ptdNetSalesSameDayLastYear: 'price',
  ptdNetSalesPercentageDiffToLastYear: 'percent',
  ptdNetSalesPercentageDiffTo2YearsAgo: 'percent',
  ptdCheckCount: 'number',
  ptdCheckCountPercentageDiffToLastYear: 'percent',
  ptdCheckCountPercentageDiffTo2YearsAgo: 'percent',
  ptdCheckAverage: 'price',
  ptdCheckAveragePercentageDiffToLastYear: 'percent',
  ptdCheckAveragePercentageDiffTo2YearsAgo: 'percent',
  qtdNetSales: 'price',
  qtdNetSalesSameDayLastYear: 'price',
  qtdNetSalesPercentageDiffToLastYear: 'percent',
  qtdNetSalesPercentageDiffTo2YearsAgo: 'percent',
  qtdCheckCount: 'number',
  qtdCheckCountPercentageDiffToLastYear: 'percent',
  qtdCheckCountPercentageDiffTo2YearsAgo: 'percent',
  qtdCheckAverage: 'price',
  qtdCheckAveragePercentageDiffToLastYear: 'percent',
  qtdCheckAveragePercentageDiffTo2YearsAgo: 'percent',
  ytdNetSales: 'price',
  ytdNetSalesSameDayLastYear: 'price',
  ytdNetSalesPercentageDiffToLastYear: 'percent',
  ytdNetSalesPercentageDiffTo2YearsAgo: 'percent',
  ytdCheckCount: 'number',
  ytdCheckCountPercentageDiffToLastYear: 'percent',
  ytdCheckCountPercentageDiffTo2YearsAgo: 'percent',
  ytdCheckAverage: 'price',
  ytdCheckAveragePercentageDiffToLastYear: 'percent',
  ytdCheckAveragePercentageDiffTo2YearsAgo: 'percent',
  openedAt: 'string',
  market: 'string',
  ro: 'string',
} as const

const twoYearsStackedTypes = [
  't7',
  't30',
  'ttm',
  'pwk',
  'wtd',
  'ptd',
  'qtd',
  'ytd',
]

const query = gql`
  query ListFarwestSalesAndCompTickets(
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
    $hasGroupBy: Boolean!
  ) {
    listLocationGroupDateRangeMetricsValues(
      iDate: $iEndDate
      iFilter: $iFilter
    ) @skip(if: $hasGroupBy) {
      nodes {
        type
        locationGroupId
        metricData
        metricSummaryData
      }
    }

    listLocationDateRangeMetricsValues(iDate: $iEndDate, iFilter: $iFilter)
      @include(if: $hasGroupBy) {
      nodes {
        type
        locationId
        metricData
        metricSummaryData
      }
    }

    listLocationMarketAndOpenDate(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) @include(if: $hasGroupBy) {
      nodes {
        locationId
        openedAt
        market
        ro
      }
    }
  }
`

const priorYearQuery = gql`
  query ListFarwestPriorYearSalesAndCompTickets(
    $iStartDate: Date!
    $iEndDate: Date!
    $iFilter: JSON!
    $hasGroupBy: Boolean!
  ) {
    listLocationGroupDateRangeMetricsValues(
      iDate: $iEndDate
      iFilter: $iFilter
    ) @skip(if: $hasGroupBy) {
      nodes {
        type
        locationGroupId
        metricData
        metricSummaryData
      }
    }

    listLocationDateRangeMetricsValues(iDate: $iEndDate, iFilter: $iFilter)
      @include(if: $hasGroupBy) {
      nodes {
        type
        locationId
        metricData
        metricSummaryData
      }
    }

    listLocationMarketAndOpenDate(
      iStartDate: $iStartDate
      iEndDate: $iEndDate
      iFilter: $iFilter
    ) @include(if: $hasGroupBy) {
      nodes {
        locationId
        openedAt
        market
        ro
      }
    }
  }
`

const useCommonFwWingstopSalesAndCompTickets = buildMetricValuesHook({
  groupFilterType: 'corporate',
  metrics: {
    codes: [
      'net_sales',
      'net_sales_same_day_last_year',
      'net_sales_same_day_2_years_ago',
      'net_sales_7_days_ago',
      'net_sales_percentage_diff_to_7_days_ago',
      'net_sales_percentage_diff_to_last_year',
      { key: 'net_sales_percentage_diff_to_last_year', type: 'yoy' },
      'check_count',
      'check_count_same_day_last_year',
      'check_count_same_day_2_years_ago',
      'check_count_7_days_ago',
      'check_count_percentage_diff_to_7_days_ago',
      'check_count_percentage_diff_to_last_year',
      { key: 'check_count_percentage_diff_to_last_year', type: 'yoy' },
      'check_average',
      'check_average_same_day_last_year',
      'check_average_same_day_2_years_ago',
      'check_average_7_days_ago',
      'check_average_percentage_diff_to_7_days_ago',
      'check_average_percentage_diff_to_last_year',
      { key: 'check_average_percentage_diff_to_last_year', type: 'yoy' },
    ],
  },
  handler: (data) => ({
    ...data,
    netSalesPercentageDiffTo2YearsAgo:
      calc(
        data.netSalesPercentageDiffToLastYear,
        '+',
        data.yoyNetSalesPercentageDiffToLastYear,
      ) || null,
    checkCountPercentageDiffTo2YearsAgo:
      calc(
        data.checkCountPercentageDiffToLastYear,
        '+',
        data.yoyCheckCountPercentageDiffToLastYear,
      ) || null,
    checkAveragePercentageDiffTo2YearsAgo:
      calc(
        data.checkAveragePercentageDiffToLastYear,
        '+',
        data.yoyCheckAveragePercentageDiffToLastYear,
      ) || null,
  }),
  hasSummary: true,
})

const useFwWingstopSalesAndCompTickets = () => {
  const { startDate, endDate } = useDateFilter()
  const { groupFilter, hasGroupBy } = useGroupFilter()
  const { data, loading } = useCommonFwWingstopSalesAndCompTickets()
  const { data: storeDetailsData, loading: storeDetailLoading } =
    useQuery<IStoreDetailsType>(query, {
      variables: {
        iStartDate: startDate,
        iEndDate: endDate,
        iFilter: {
          location_group_ids: hasGroupBy
            ? groupFilter?.ids
            : groupFilter?.list?.map((g) => g.id),
          intersected_location_group_ids: groupFilter?.intersectedIds,
          metrics: [
            'net_sales',
            'net_sales_7_days_ago',
            'net_sales_same_day_last_year',
            'net_sales_percentage_diff_to_last_year',
            'check_count',
            'check_count_percentage_diff_to_last_year',
            'check_average',
            'check_average_percentage_diff_to_last_year',
          ],
        },
        hasGroupBy,
      },
      skip: !startDate || !endDate || !groupFilter,
    })
  const { data: storePriorDetailsData, loading: storePriorDetailLoading } =
    useQuery<IStoreDetailsType>(priorYearQuery, {
      variables: {
        iStartDate: startDate,
        iEndDate: endDate,
        iFilter: {
          location_group_ids: hasGroupBy
            ? groupFilter?.ids
            : groupFilter?.list?.map((g) => g.id),
          intersected_location_group_ids: groupFilter?.intersectedIds,
          metrics: [
            'net_sales_percentage_diff_to_last_year',
            'check_count_percentage_diff_to_last_year',
            'check_average_percentage_diff_to_last_year',
          ],
          use_yoy: true,
        },
        hasGroupBy,
      },
      skip: !startDate || !endDate || !groupFilter,
    })

  return {
    data: useMemo((): IApiDataType => {
      if (!data) return data

      const locationDetails =
        storeDetailsData?.listLocationMarketAndOpenDate?.nodes

      const dateRangeData = hasGroupBy
        ? storeDetailsData?.listLocationDateRangeMetricsValues?.nodes
        : storeDetailsData?.listLocationGroupDateRangeMetricsValues?.nodes

      const priorYearDateRangeData = hasGroupBy
        ? storePriorDetailsData?.listLocationDateRangeMetricsValues?.nodes
        : storePriorDetailsData?.listLocationGroupDateRangeMetricsValues?.nodes

      return data.map((d) => {
        const marketDetail = locationDetails?.find(
          (l) => l.locationId === (d.location as { id: number })?.id,
        )

        const extendedData =
          dateRangeData?.reduce((acc, item) => {
            const dataLocationOrLocationGroupId = hasGroupBy
              ? (d.location as { id: number })?.id
              : (d.locationGroup as { id: number })?.id
            const dateRangeDataLocationOrLocationGroupId = hasGroupBy
              ? (item as { locationId: number }).locationId
              : (item as { locationGroupId: number }).locationGroupId

            if (
              dataLocationOrLocationGroupId &&
              dateRangeDataLocationOrLocationGroupId &&
              dataLocationOrLocationGroupId ===
                dateRangeDataLocationOrLocationGroupId
            ) {
              for (const [metricKey, metricData] of Object.entries(
                item.metricData,
              )) {
                const key = _.camelCase(`${item.type}_${metricKey}`)

                if (key === 't7NetSales')
                  acc['averageNetSalesTrailing7Days'] =
                    (metricData.value / 7) * 100
                if (key === 't7NetSalesSameDayLastYear')
                  acc['averageNetSalesTrailing7DaysLastYear'] =
                    (metricData.value / 7) * 100

                acc[key] = metricData.value

                if (
                  ['DOLLAR', 'PERCENT', 'PERCENTAGE'].includes(metricData.unit)
                )
                  acc[key] = metricData.value * 100
              }
            }

            if (d.id === 'summary' && item.metricSummaryData) {
              for (const [metricKey, metricData] of Object.entries(
                item.metricSummaryData,
              )) {
                const key = _.camelCase(`${item.type}_${metricKey}`)

                if (key === 't7NetSales')
                  acc['averageNetSalesTrailing7Days'] =
                    (metricData.value / 7) * 100
                if (key === 't7NetSalesSameDayLastYear')
                  acc['averageNetSalesTrailing7DaysLastYear'] =
                    (metricData.value / 7) * 100

                acc[key] = metricData.value

                if (
                  ['DOLLAR', 'PERCENT', 'PERCENTAGE'].includes(metricData.unit)
                )
                  acc[key] = metricData.value * 100
              }
            }

            return acc
          }, {} as Record<string, number>) || {}

        const twoYearsStackedValue =
          twoYearsStackedTypes.reduce((result, type) => {
            const dataLocationOrLocationGroupId = hasGroupBy
              ? (d.location as { id: number })?.id
              : (d.locationGroup as { id: number })?.id

            const currentData = dateRangeData?.find((d) =>
              hasGroupBy
                ? (d as { locationId: number }).locationId ===
                    dataLocationOrLocationGroupId && d.type === type
                : (d as { locationGroupId: number }).locationGroupId ===
                    dataLocationOrLocationGroupId && d.type === type,
            )

            const priorData = priorYearDateRangeData?.find((d) =>
              hasGroupBy
                ? (d as { locationId: number }).locationId ===
                    dataLocationOrLocationGroupId && d.type === type
                : (d as { locationGroupId: number }).locationGroupId ===
                    dataLocationOrLocationGroupId && d.type === type,
            )

            if (currentData && priorData) {
              for (const rawMetricKey of [
                'net_sales',
                'check_count',
                'check_average',
              ]) {
                const metricKey = `${rawMetricKey}_percentage_diff_to_last_year`
                const currentValue = currentData.metricData[metricKey]?.value
                const priorValue = priorData.metricData[metricKey]?.value

                result[
                  _.camelCase(
                    `${type}_${rawMetricKey}_percentage_diff_to_2_years_ago`,
                  )
                ] =
                  currentValue && priorValue
                    ? (currentValue + priorValue) * 100
                    : null
              }
            }

            if (d.id === 'summary') {
              for (const rawMetricKey of [
                'net_sales',
                'check_count',
                'check_average',
              ]) {
                const metricKey = `${rawMetricKey}_percentage_diff_to_last_year`

                const currentValue = dateRangeData?.find(
                  (d) => d.type === type && d.metricSummaryData,
                )?.metricSummaryData[metricKey]?.value

                const priorValue = priorYearDateRangeData?.find(
                  (d) => d.type === type && d.metricSummaryData,
                )?.metricSummaryData[metricKey]?.value

                result[
                  _.camelCase(
                    `${type}_${rawMetricKey}_percentage_diff_to_2_years_ago`,
                  )
                ] =
                  currentValue && priorValue
                    ? (currentValue + priorValue) * 100
                    : null
              }
            }

            return result
          }, {} as Record<string, number | null>) || {}

        const storeOpenDate =
          d.id === 'summary' ? moment('-') : moment(marketDetail?.openedAt)

        return {
          ...d,
          openedAt: storeOpenDate.isValid()
            ? storeOpenDate.format('YYYY/MM/DD')
            : '-',
          ro: marketDetail?.ro,
          market: marketDetail?.market,
          ...extendedData,
          ...twoYearsStackedValue,
        }
      })
    }, [data, storeDetailsData, storePriorDetailsData]),
    loading: loading || storeDetailLoading || storePriorDetailLoading,
  }
}

export default useFwWingstopSalesAndCompTickets
